import type { AnimationDebugState } from '../AnimationController';
import { logger } from '../../core/Logger';

export type RecordingState = 'idle' | 'recording' | 'paused';

export interface TimelineFrame {
  timestamp: number; // milliseconds
  state: AnimationDebugState;
  currentState: string; // Character state (idle, walk, etc.)
  currentPhase: string | null; // windup, active, release, recovery
}

export interface RecordingSession {
  version: 1;
  characterId: string;
  framerate: number;
  frames: TimelineFrame[];
  startTime: number;
  endTime: number;
}

/**
 * RecordingManager - Animation Recording System
 * 
 * Manages recording of animation debug states with configurable framerate.
 * Supports manual start/stop, frame subsampling, and session export/import.
 */
export class RecordingManager {
  private state: RecordingState = 'idle';
  private framerate: number = 60; // fps
  private windowSeconds: number = 10; // Rolling window duration
  private frames: TimelineFrame[] = [];
  private maxFrames: number;
  private lastFrameTime: number = 0;
  private frameInterval: number;
  private startTime: number = 0;
  private characterId: string = '';

  constructor(framerate = 60, windowSeconds = 10) {
    this.framerate = framerate;
    this.windowSeconds = windowSeconds;
    this.maxFrames = framerate * windowSeconds;
    this.frameInterval = 1000 / framerate;
  }

  /**
   * Start recording
   */
  start(characterId: string): void {
    this.state = 'recording';
    this.characterId = characterId;
    this.frames = [];
    this.lastFrameTime = performance.now();
    this.startTime = this.lastFrameTime;
  }

  /**
   * Pause recording (keeps existing frames)
   */
  pause(): void {
    if (this.state === 'recording') {
      this.state = 'paused';
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.state === 'paused') {
      this.state = 'recording';
      this.lastFrameTime = performance.now();
    }
  }

  /**
   * Stop recording and clear frames
   */
  stop(): void {
    this.state = 'idle';
    this.frames = [];
    this.lastFrameTime = 0;
    this.startTime = 0;
  }

  /**
   * Reset frames without stopping
   */
  reset(): void {
    this.frames = [];
    this.lastFrameTime = performance.now();
    this.startTime = this.lastFrameTime;
  }

  /**
   * Add a frame with subsampling based on framerate
   */
  addFrame(debugState: AnimationDebugState, currentState: string, currentPhase: string | null): void {
    if (this.state !== 'recording') return;

    const now = performance.now();
    const elapsed = now - this.lastFrameTime;

    // Subsample based on framerate
    if (elapsed < this.frameInterval) {
      return;
    }

    const frame: TimelineFrame = {
      timestamp: now - this.startTime,
      state: debugState,
      currentState,
      currentPhase,
    };

    this.frames.push(frame);

    // Maintain rolling window
    if (this.frames.length > this.maxFrames) {
      this.frames.shift();
      // Adjust start time to reflect the new first frame
      if (this.frames.length > 0) {
        this.startTime = now - this.frames[this.frames.length - 1].timestamp;
      }
    }

    this.lastFrameTime = now;
  }

  /**
   * Set framerate and update related values
   */
  setFramerate(fps: number): void {
    this.framerate = fps;
    this.frameInterval = 1000 / fps;
    this.maxFrames = fps * this.windowSeconds;
    
    // Trim existing frames if needed
    if (this.frames.length > this.maxFrames) {
      const removeCount = this.frames.length - this.maxFrames;
      this.frames.splice(0, removeCount);
    }
  }

  /**
   * Set window duration in seconds
   */
  setWindowSeconds(seconds: number): void {
    this.windowSeconds = seconds;
    this.maxFrames = this.framerate * seconds;
    
    // Trim existing frames if needed
    if (this.frames.length > this.maxFrames) {
      const removeCount = this.frames.length - this.maxFrames;
      this.frames.splice(0, removeCount);
    }
  }

  /**
   * Export session as JSON string
   */
  exportSession(): string {
    const session: RecordingSession = {
      version: 1,
      characterId: this.characterId,
      framerate: this.framerate,
      frames: this.frames,
      startTime: this.startTime,
      endTime: this.frames.length > 0 
        ? this.frames[this.frames.length - 1].timestamp 
        : 0,
    };

    return JSON.stringify(session, null, 2);
  }

  /**
   * Import session from JSON string
   */
  importSession(json: string): boolean {
    try {
      const session = JSON.parse(json) as RecordingSession;
      
      if (session.version !== 1) {
        logger.animation.warn('Unsupported session version:', session.version);
        return false;
      }

      this.characterId = session.characterId;
      this.framerate = session.framerate;
      this.frames = session.frames;
      this.startTime = session.startTime;
      this.frameInterval = 1000 / this.framerate;
      this.maxFrames = this.framerate * this.windowSeconds;
      this.state = 'paused'; // Imported sessions start paused

      return true;
    } catch (error) {
      logger.animation.error('Failed to import session:', error);
      return false;
    }
  }

  /**
   * Get current state
   */
  getState(): RecordingState {
    return this.state;
  }

  /**
   * Get all frames
   */
  getFrames(): TimelineFrame[] {
    return this.frames;
  }

  /**
   * Get framerate
   */
  getFramerate(): number {
    return this.framerate;
  }

  /**
   * Get duration in seconds
   */
  getDuration(): number {
    if (this.frames.length === 0) return 0;
    return this.frames[this.frames.length - 1].timestamp / 1000;
  }

  /**
   * Get total frame count
   */
  getFrameCount(): number {
    return this.frames.length;
  }

  /**
   * Get character ID
   */
  getCharacterId(): string {
    return this.characterId;
  }
}
